import { useCallback, useEffect, useMemo } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TeamsAvailability,
  TeamsBlockNode,
  TeamsChatPrefs,
  TeamsChatSummary,
  TeamsConnectionStatus,
  TeamsMessage,
  TeamsMessagePage,
} from '@atlas/shared';

import { useTeamsRealtime } from './useTeamsRealtime';
import { api, ApiError } from '../services/api';
import { useTeamsChatStore } from '../stores/teamsChatStore';
import {
  OPTIMISTIC_MESSAGE_ID_PREFIX,
  isOptimisticMessage,
} from '../components/teamsChat/TeamsChatThread';

/**
 * Deliberately more than the sidebar shows, because the one response feeds
 * *both* sections: People and Groups each show 20 before "see more", and they
 * are drawn from a single recency-ordered list. Ask for only 40 and someone
 * whose recent activity is mostly group chats would see a short People section
 * with conversations missing from it — which reads as a bug, not a limit.
 *
 * 60 rather than the server's 100 ceiling: Graph serves ~25 chats per page, so
 * this is three round trips at worst and usually fewer, and every extra page is
 * latency on first open plus a slice of a throttle budget shared with the rest
 * of the integration. The sections page client-side from here, so a bigger
 * number buys reach into old conversations — which is what Teams itself is for.
 */
/** What one send carries: the document, and its plain-text rendering. */
interface SendInput {
  text: string;
  nodes: TeamsBlockNode[];
}

const CHATS_PAGE_SIZE = 60;
const MESSAGES_PAGE_SIZE = 30;
/**
 * Poll intervals. Two sets, because polling is now the *fallback*, not the
 * mechanism: `teams:activity` pushes drive the refresh when the Graph change
 * subscription is alive.
 *
 * Polling is deliberately not removed. The subscription can fail in ways the
 * browser cannot see — tenant policy, licensing, a webhook the Graph service
 * cannot reach, or a subscription that expired before the renewal job caught it
 * (they cap at 60 minutes). Externally-initiated chats are never covered by
 * `/chats/getAllMessages` at all. And none of this is exercised in CI, so a
 * silent regression would only surface as "the panel stopped updating". The
 * slow interval is the floor that keeps that from being permanent.
 */
const CHATS_POLL_MS = 60_000;
const MESSAGES_POLL_MS = 15_000;
/**
 * Used once the server reports live updates are actually active. Ten minutes /
 * two minutes is slow enough to be negligible against the Graph throttle, fast
 * enough that a dead subscription costs a couple of minutes of staleness rather
 * than an unbounded amount.
 */
const CHATS_POLL_LIVE_MS = 600_000;
const MESSAGES_POLL_LIVE_MS = 120_000;
/**
 * Presence poll. Deliberately the slowest thing in the panel.
 *
 * Availability genuinely changes minute to minute, so any interval we could
 * afford is already "slightly stale" — chasing accuracy here would mean polling
 * every few seconds, and that spends the user's Graph throttle budget on the
 * least consequential pixel on screen. Two minutes keeps the dots roughly
 * honest for the "can I interrupt them" glance they exist to serve, and a wrong
 * dot costs a message sent to someone who just stepped away. Nothing more.
 *
 * Not wired to the phase-4 socket event: Graph change notifications cover
 * messages, not presence, so a push tells us nothing about availability.
 */
const PRESENCE_POLL_MS = 120_000;
/**
 * Mirrors the route's cap. It cannot bite today — a page of chats is 30 — but
 * the server *rejects* an over-long list rather than truncating it, so if the
 * page size ever grows past this the failure would be every dot disappearing at
 * once. Trimming here makes that degrade to "the last few have no dot".
 */
const PRESENCE_BATCH_LIMIT = 50;

/** Shared query key so the toggle and the panel reuse one status fetch. */
const STATUS_KEY = ['teams', 'status'] as const;

/**
 * How many conversations have something the user has not seen.
 *
 * Reads the chat list the panel already owns instead of fetching: the AppBar
 * toggle renders on every page, and giving it its own query would double the
 * Graph traffic for a number that is a projection of data already in the cache.
 * Zero until that query has loaded, which is the right answer for "we do not
 * know yet" — a badge that flashes a guess and corrects itself is worse than
 * one that appears a moment late.
 *
 * A count of conversations, NOT of messages: see `TeamsChatSummary.hasUnread`
 * for why Graph cannot cheaply tell us the latter.
 */
export function useTeamsUnreadCount(): number {
  const { data } = useQuery({
    queryKey: ['teams', 'chats', CHATS_PAGE_SIZE],
    queryFn: () => api.teams.listChats({ limit: CHATS_PAGE_SIZE }),
    // Never fetches on its own account. The panel's own copy of this query owns
    // the fetching policy; this one is a cache subscription wearing a query's
    // clothes, so the toggle cannot start Graph traffic on a page where the
    // panel is not mounted or the user is not connected.
    enabled: false,
    notifyOnChangeProps: ['data'],
  });

  // Muted conversations are excluded: muting is a request to stop being alerted,
  // and a badge is the alert. `hasUnread` itself stays true on the server, so
  // unmuting immediately tells the truth again rather than a stale version of it.
  return useMemo(
    () => (data ?? []).filter((chat) => chat.hasUnread && !chat.isMuted).length,
    [data],
  );
}

/**
 * A 409 from any chat call means the stored refresh token is dead. It must be
 * surfaced as "reconnect", never as an empty list — an empty list reads as
 * "you have no conversations", which is a lie the user cannot act on.
 */
function isReconnectError(error: unknown): boolean {
  return error instanceof ApiError && error.statusCode === 409;
}

function errorMessageOf(error: unknown): string | undefined {
  if (!error) return undefined;
  return error instanceof Error ? error.message : String(error);
}

/** Cache shape of the messages infinite query, for the optimistic writes. */
type MessagesCache = InfiniteData<TeamsMessagePage, string | undefined>;

/**
 * A local echo of a message we have just posted but Graph has not confirmed.
 * `isSelf` puts it on the user's side of the thread; the id prefix is what
 * marks it as still in flight.
 */
function buildOptimisticMessage(text: string): TeamsMessage {
  return {
    id: `${OPTIMISTIC_MESSAGE_ID_PREFIX}${crypto.randomUUID()}`,
    createdAt: new Date().toISOString(),
    // Self bubbles never render an author name, so there is nothing to guess.
    authorName: '',
    text,
    isSelf: true,
    isSystemMessage: false,
    deleted: false,
  };
}

export interface UseTeamsStatusResult {
  configured: boolean;
  connected: boolean;
  needsReconnect: boolean;
  /**
   * Whether the server has a live Graph subscription for this user.
   * `undefined` means the server did not say — treat that as "assume not", so
   * an older server (or one that drops the field) keeps the fast poll rather
   * than silently going quiet.
   */
  liveUpdates: boolean | undefined;
  /**
   * Whether this user's token actually carries `Presence.Read.All`. Defaults to
   * false: a missing flag must hide the presence affordance, never render dots
   * that would be permanently unknown.
   */
  presenceAvailable: boolean;
  /**
   * True only when the token carries `User.ReadBasic.All` (consented
   * 2026-07-23). Still false for anyone who connected before that date — a
   * grant does not upgrade a token already issued.
   */
  avatarsAvailable: boolean;
  /**
   * Whether the new-chat picker can search the company directory. Same scope as
   * `avatarsAvailable`, separate flag — the picker's wording depends on it, and
   * with it false it must go on promising only "people you've chatted with".
   */
  directoryAvailable: boolean;
  /** True only when the token carries `Files.Read.All` (consented 2026-07-22). */
  filesAvailable: boolean;
  isLoading: boolean;
}

/**
 * Read the live-updates flag without depending on it existing.
 *
 * The field is being added to the status payload by the backend half of this
 * phase; reading it structurally rather than off the shared type means the
 * frontend compiles and behaves sanely whichever lands first, and degrades to
 * "unknown" if it is ever removed.
 */
function readLiveUpdates(status: TeamsConnectionStatus | undefined): boolean | undefined {
  if (!status) return undefined;
  const value = (status as unknown as Record<string, unknown>).liveUpdates;
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * Teams connection status. Gates the whole feature: when the server has no
 * Teams credentials at all we render nothing — no toggle, no drawer.
 */
export function useTeamsStatus(enabled: boolean): UseTeamsStatusResult {
  const { data, isLoading } = useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => api.teams.getStatus(),
    enabled,
    staleTime: 60_000,
  });

  return {
    configured: data?.configured ?? false,
    connected: data?.connected ?? false,
    needsReconnect: data?.needsReconnect ?? false,
    liveUpdates: readLiveUpdates(data),
    presenceAvailable: data?.presenceAvailable ?? false,
    avatarsAvailable: data?.avatarsAvailable ?? false,
    directoryAvailable: data?.directoryAvailable ?? false,
    filesAvailable: data?.filesAvailable ?? false,
    isLoading,
  };
}

export interface UseTeamsChatSessionOptions {
  /** False on the login screen — status is a 401 there, so don't ask. */
  enabled?: boolean;
  /**
   * Whether the chat is actually on screen right now.
   *
   * This is what replaced the drawer's `isOpen`: the standalone page has no
   * closed state, so the gate had to become "is anyone looking at this" rather
   * than "is the drawer open". Everything server-touching hangs off it — a
   * false value must still mean zero Graph traffic, which is the property the
   * closed drawer relied on.
   */
  isVisible: boolean;
}

/**
 * Everything a Teams chat renderer needs. Deliberately flat and presentational:
 * no query objects leak out, so the drawer and the standalone page cannot drift
 * apart by reaching for different bits of React Query state.
 */
export interface TeamsChatSession {
  /** False when the server has no Teams credentials at all. */
  configured: boolean;
  connected: boolean;
  /** Token is dead — status said so, or a 409 came back from a call. */
  needsReconnect: boolean;

  chats: TeamsChatSummary[];
  isChatsLoading: boolean;
  chatsErrorMessage: string | undefined;

  activeChatId: string | null;
  activeChat: TeamsChatSummary | undefined;
  messages: TeamsMessage[];
  isMessagesLoading: boolean;
  messagesErrorMessage: string | undefined;
  hasMoreMessages: boolean;
  isLoadingMoreMessages: boolean;

  /** Undefined when presence is unavailable or has not resolved. */
  presence: Map<string, TeamsAvailability> | undefined;
  /**
   * Whether profile photos can be fetched at all. False for every connection
   * today — `User.ReadBasic.All` is not consented — so avatars render as
   * initials and no request is made.
   */
  avatarsAvailable: boolean;
  /** Whether chat attachments can be downloaded at all — see `TeamsFileAttachment`. */
  filesAvailable: boolean;

  isSending: boolean;
  didSendFail: boolean;

  selectChat: (chatId: string) => void;
  /**
   * Favourite or mute a conversation. Fire-and-forget: it writes through
   * optimistically and reconciles against the server, so nothing awaits it.
   */
  setChatPrefs: (chatId: string, prefs: TeamsChatPrefs) => void;
  /** Clear a conversation's unread badge without opening it. Fire-and-forget. */
  markRead: (chatId: string) => void;
  /** Clear every conversation's unread badge. Fire-and-forget. */
  markAllRead: () => void;
  /**
   * Mute or unmute a whole set of conversations. Fire-and-forget, and
   * sequential on the wire — see the implementation for why.
   */
  setChatsMuted: (chatIds: string[], muted: boolean) => void;
  clearActiveChat: () => void;
  loadMoreMessages: () => void;
  retryChats: () => void;
  retryMessages: () => void;
  /**
   * Rejects on failure, which is how the composer knows to keep the draft.
   *
   * `nodes` is the document model the composer produced; `text` is its
   * plain-text rendering, which is what the optimistic echo shows.
   */
  send: (text: string, nodes: TeamsBlockNode[]) => Promise<void>;
  dismissSendError: () => void;
}

/**
 * The whole Teams chat data layer — status, chats, messages, sending, presence
 * and the phase-4 push subscription — with no opinion about how it is framed.
 *
 * Extracted from `TeamsChatPanel` so the drawer and the standalone `/teams-chat`
 * page are the *same* session logic behind two different chromes. Two windows
 * running two copies of this is fine and expected: the server broadcasts
 * `teams:activity` to `user:<userId>`, so each instance invalidates its own
 * cache off the same push rather than needing to talk to the other.
 */
export function useTeamsChatSession({
  enabled = true,
  isVisible,
}: UseTeamsChatSessionOptions): TeamsChatSession {
  const queryClient = useQueryClient();

  const activeChatId = useTeamsChatStore((s) => s.activeChatId);
  const setActiveChat = useTeamsChatStore((s) => s.setActiveChat);
  const clearActiveChat = useTeamsChatStore((s) => s.clearActiveChat);

  const {
    configured,
    connected,
    needsReconnect,
    liveUpdates,
    presenceAvailable,
    avatarsAvailable,
    filesAvailable,
  } = useTeamsStatus(enabled);

  /**
   * Two gates, not one, and the distinction is the whole unread badge.
   *
   * `canFetchChats` is about the *connection*: the list may load whenever Teams
   * is usable, drawer open or not, because the AppBar badge has to be able to
   * light up while the panel is shut. `isViewing` is about *attention*, and is
   * what still gates polling and the message thread.
   */
  const canFetchChats = configured && connected && !needsReconnect;
  const isViewing = isVisible && canFetchChats;
  // Only back off when the server positively confirms a live subscription.
  const isLive = liveUpdates === true;

  const chatsQuery = useQuery({
    queryKey: ['teams', 'chats', CHATS_PAGE_SIZE],
    queryFn: () => api.teams.listChats({ limit: CHATS_PAGE_SIZE }),
    // Enabled while closed so the badge can be earned, but the *interval* is
    // still attention-gated: a closed panel refreshes on push only. Background-
    // polling Graph for every connected user, forever, to keep a badge warm is
    // exactly the cost this panel was built to avoid.
    //
    // The consequence is honest: with live subscriptions down, a closed panel's
    // badge goes stale until it is reopened. Opening it is always correct.
    enabled: canFetchChats,
    refetchInterval: isViewing ? (isLive ? CHATS_POLL_LIVE_MS : CHATS_POLL_MS) : false,
    staleTime: 30_000,
    retry: (failureCount, error) => !isReconnectError(error) && failureCount < 2,
  });

  const canFetchMessages = isViewing && Boolean(activeChatId);

  const messagesKey = useMemo(() => ['teams', 'chat-messages', activeChatId], [activeChatId]);

  const sendMutation = useMutation<TeamsMessage, Error, SendInput, { previous?: MessagesCache }>({
    mutationFn: ({ text, nodes }) => api.teams.sendMessage(activeChatId as string, text, nodes),
    onMutate: async ({ text }) => {
      // Stop any in-flight poll from landing on top of the echo we are about
      // to write — a refetch replaces the whole page set, echo included.
      await queryClient.cancelQueries({ queryKey: messagesKey });
      const previous = queryClient.getQueryData<MessagesCache>(messagesKey);

      queryClient.setQueryData<MessagesCache>(messagesKey, (current) => {
        if (!current || current.pages.length === 0) return current;
        // Pages are newest-first and page 0 holds the newest, so the echo goes
        // at the very front of page 0.
        const [newest, ...rest] = current.pages;
        return {
          ...current,
          pages: [
            { ...newest, messages: [buildOptimisticMessage(text), ...newest.messages] },
            ...rest,
          ],
        };
      });

      return { previous };
    },
    onError: (_error, _input, context) => {
      // Put the thread back exactly as it was; the composer keeps the text.
      if (context?.previous) queryClient.setQueryData(messagesKey, context.previous);
    },
    onSettled: () => {
      // The refetch replaces every page wholesale, which is what retires the
      // echo — we never splice the server's copy in by hand, because doing so
      // on top of a poll that already carried it is exactly how a message ends
      // up in the thread twice.
      void queryClient.invalidateQueries({ queryKey: messagesKey });
      // A sent message also changes the conversation's preview and ordering.
      void queryClient.invalidateQueries({ queryKey: ['teams', 'chats'] });
    },
    retry: false,
  });

  const isSending = sendMutation.isPending;

  const messagesQuery = useInfiniteQuery<
    TeamsMessagePage,
    Error,
    { pages: TeamsMessagePage[] },
    readonly unknown[],
    string | undefined
  >({
    queryKey: messagesKey,
    queryFn: ({ pageParam }) =>
      api.teams.listMessages(activeChatId as string, {
        limit: MESSAGES_PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: canFetchMessages,
    // Polling is suspended for the duration of a send. A poll firing between
    // the optimistic insert and the settle-invalidation would replace the pages
    // with a server copy that does not have the message yet, so the echo would
    // blink out and back — the send would look like it had failed.
    refetchInterval:
      canFetchMessages && !isSending
        ? isLive
          ? MESSAGES_POLL_LIVE_MS
          : MESSAGES_POLL_MS
        : false,
    retry: (failureCount, error) => !isReconnectError(error) && failureCount < 2,
  });

  // Push updates. `isViewing` governs the message thread only — an off-screen
  // thread marks its cache stale rather than firing a Graph request, and a
  // notification landing mid-send waits for the send to settle rather than
  // wiping the optimistic echo. The chat list is refreshed either way, because
  // that is what the unread badge is read from; `isConnected` is what stops a
  // dead connection from retrying into a 409.
  useTeamsRealtime({
    isViewing,
    isConnected: canFetchChats,
    activeChatId: activeChatId ?? null,
    isSending,
  });

  const chats: TeamsChatSummary[] = useMemo(() => chatsQuery.data ?? [], [chatsQuery.data]);

  /**
   * The people actually on screen — nothing more. Sorted so the array is a
   * stable query key: the chat list reorders on every new message, and keying
   * off its order would refetch presence each time a message arrives.
   */
  const presenceUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const chat of chats) {
      if (chat.presenceUserId) ids.add(chat.presenceUserId);
    }
    // Trim by RECENCY, then sort. `chats` arrives newest-first, so the people
    // who lose their dot when there are more than the batch allows are the ones
    // furthest down the list rather than whoever happens to sort last by id.
    // Sorting afterwards is what keeps the query key stable as chats reorder.
    return [...ids].slice(0, PRESENCE_BATCH_LIMIT).sort();
  }, [chats]);

  // Attention-gated, plus the scope check. Unlike the chat list this stays tied
  // to `isViewing`: a dot nobody can see is worth nothing, and presence is the
  // most expensive poll in the panel. A token issued before `Presence.Read.All`
  // was granted must cost exactly this one feature.
  const canFetchPresence = isViewing && presenceAvailable && presenceUserIds.length > 0;

  const presenceQuery = useQuery({
    queryKey: ['teams', 'presence', presenceUserIds],
    queryFn: () => api.teams.getPresence(presenceUserIds),
    enabled: canFetchPresence,
    refetchInterval: canFetchPresence ? PRESENCE_POLL_MS : false,
    staleTime: PRESENCE_POLL_MS,
    // No retry, and the error is never read: a presence failure must be
    // completely silent. The chat list keeps working, the dots simply do not
    // appear, and nothing about it reaches the panel's error UI.
    retry: false,
  });

  const presence: Map<string, TeamsAvailability> | undefined = useMemo(() => {
    if (!presenceAvailable || !presenceQuery.data) return undefined;
    return new Map(presenceQuery.data.map((entry) => [entry.userId, entry.availability]));
  }, [presenceAvailable, presenceQuery.data]);

  const messages: TeamsMessage[] = useMemo(() => {
    const flat = (messagesQuery.data?.pages ?? []).flatMap((page) => page.messages);
    // Belt and braces against a double render of the same message: if the
    // server's copy of an echoed message has already arrived (a poll that raced
    // the settle-invalidation), drop the echo rather than showing both. Text +
    // isSelf is the only join available — the send contract carries no
    // client-supplied id to correlate on.
    const confirmedSelfText = new Set(
      flat
        .filter((message) => message.isSelf && !isOptimisticMessage(message))
        .map((message) => message.text.trim()),
    );
    return flat.filter(
      (message) => !isOptimisticMessage(message) || !confirmedSelfText.has(message.text.trim()),
    );
  }, [messagesQuery.data]);

  const activeChat = useMemo(
    () => chats.find((chat) => chat.id === activeChatId),
    [chats, activeChatId],
  );

  /**
   * Record that this conversation has been read, and clear its badge now rather
   * than waiting for the round trip — the user has the chat open in front of
   * them, so an unread marker on it is already wrong.
   *
   * A failure is swallowed on purpose. The worst case is that the badge returns
   * on the next list refetch, which is both self-correcting and the truth as the
   * server currently knows it; an error toast for "we could not remember that
   * you looked at this" would be noise about something the user cannot fix.
   */
  const markRead = useCallback(
    (chatId: string) => {
      queryClient.setQueryData<TeamsChatSummary[]>(['teams', 'chats', CHATS_PAGE_SIZE], (current) =>
        current?.map((chat) =>
          chat.id === chatId && chat.hasUnread ? { ...chat, hasUnread: false } : chat,
        ),
      );
      void api.teams.markChatRead(chatId).catch(() => undefined);
    },
    [queryClient],
  );

  /** `markRead` for every conversation at once, failing just as quietly. */
  const markAllRead = useCallback(() => {
    queryClient.setQueryData<TeamsChatSummary[]>(['teams', 'chats', CHATS_PAGE_SIZE], (current) =>
      current?.map((chat) => (chat.hasUnread ? { ...chat, hasUnread: false } : chat)),
    );
    void api.teams.markAllChatsRead().catch(() => undefined);
  }, [queryClient]);

  const selectChat = useCallback(
    (chatId: string) => {
      setActiveChat(chatId);
      markRead(chatId);
    },
    [setActiveChat, markRead],
  );

  /**
   * Favourite or mute a conversation.
   *
   * Written through optimistically because these back a menu the user watches
   * close: waiting a round trip to see a star fill in reads as a dead click. The
   * server's answer then replaces the guess, so a refusal (the per-user cap) or
   * a race settles on what is actually stored rather than on what we hoped.
   *
   * On failure the cache is invalidated rather than hand-rolled back — the next
   * fetch is authoritative, and reconstructing the previous value here would be
   * a second, divergent copy of the same state.
   */
  const setChatPrefs = useCallback(
    (chatId: string, prefs: TeamsChatPrefs) => {
      const patch = (next: Partial<TeamsChatSummary>) => {
        queryClient.setQueryData<TeamsChatSummary[]>(
          ['teams', 'chats', CHATS_PAGE_SIZE],
          (current) => current?.map((chat) => (chat.id === chatId ? { ...chat, ...next } : chat)),
        );
      };

      patch({
        ...(prefs.favorite !== undefined ? { isFavorite: prefs.favorite } : {}),
        ...(prefs.muted !== undefined ? { isMuted: prefs.muted } : {}),
      });

      void api.teams
        .setChatPrefs(chatId, prefs)
        .then((result) => patch({ isFavorite: result.isFavorite, isMuted: result.isMuted }))
        .catch(() => {
          void queryClient.invalidateQueries({ queryKey: ['teams', 'chats'] });
        });
    },
    [queryClient],
  );

  /**
   * Mute or unmute several conversations at once — what the Groups header's
   * menu is for.
   *
   * The requests go out **one at a time**. The server keeps a single list of
   * muted chat ids per user and rewrites it whole on every request, so
   * overlapping writes would each read the same "before" list and the last one
   * home would silently drop every other chat's change.
   *
   * ponytail: one request per chat. A bulk route if a section ever grows big
   * enough for the round trips to be felt.
   */
  const setChatsMuted = useCallback(
    (chatIds: string[], muted: boolean) => {
      if (chatIds.length === 0) return;
      const targets = new Set(chatIds);
      queryClient.setQueryData<TeamsChatSummary[]>(
        ['teams', 'chats', CHATS_PAGE_SIZE],
        (current) => current?.map((chat) => (targets.has(chat.id) ? { ...chat, isMuted: muted } : chat)),
      );

      void (async () => {
        try {
          for (const chatId of chatIds) {
            await api.teams.setChatPrefs(chatId, { muted });
          }
        } finally {
          // Settle on the server's answer either way: a partial run has left
          // the panel showing more than actually landed, and a complete one is
          // cheap to confirm.
          void queryClient.invalidateQueries({ queryKey: ['teams', 'chats'] });
        }
      })().catch(() => undefined);
    },
    [queryClient],
  );

  /**
   * Keep the open thread read as it grows.
   *
   * Without this, a reply arriving in the conversation you are literally reading
   * would badge it: the chat list refetch that carries the new message also
   * carries `hasUnread: true`, and the only thing that ever cleared it was
   * selecting the chat. Keyed on the newest message id so it fires once per new
   * message rather than on every render, and gated on `isViewing` so a
   * backgrounded panel does not silently swallow the unread state of a
   * conversation nobody is actually looking at.
   */
  const newestMessageId = messages[0]?.id;
  useEffect(() => {
    if (!isViewing || !activeChatId || !newestMessageId) return;
    if (!activeChat?.hasUnread) return;
    markRead(activeChatId);
  }, [isViewing, activeChatId, newestMessageId, activeChat?.hasUnread, markRead]);

  const loadMoreMessages = useCallback(() => {
    void messagesQuery.fetchNextPage();
  }, [messagesQuery]);

  const retryChats = useCallback(() => {
    void chatsQuery.refetch();
  }, [chatsQuery]);

  const retryMessages = useCallback(() => {
    void messagesQuery.refetch();
  }, [messagesQuery]);

  const send = useCallback(
    (text: string, nodes: TeamsBlockNode[]) =>
      sendMutation.mutateAsync({ text, nodes }).then(() => undefined),
    [sendMutation],
  );

  const dismissSendError = useCallback(() => {
    sendMutation.reset();
  }, [sendMutation]);

  // A 409 anywhere is the same signal the status endpoint gives via
  // needsReconnect — the connection is dead, only reconnecting fixes it. A
  // failed send is no different: it must land on the reconnect prompt, not on
  // a generic "couldn't send" the user cannot act on.
  const sawReconnect409 =
    isReconnectError(chatsQuery.error) ||
    isReconnectError(messagesQuery.error) ||
    isReconnectError(sendMutation.error);

  return {
    configured,
    connected,
    needsReconnect: needsReconnect || sawReconnect409,
    chats,
    isChatsLoading: chatsQuery.isLoading,
    chatsErrorMessage: errorMessageOf(chatsQuery.error),
    activeChatId,
    activeChat,
    messages,
    isMessagesLoading: messagesQuery.isLoading,
    messagesErrorMessage: errorMessageOf(messagesQuery.error),
    hasMoreMessages: Boolean(messagesQuery.hasNextPage),
    isLoadingMoreMessages: messagesQuery.isFetchingNextPage,
    presence,
    avatarsAvailable,
    filesAvailable,
    isSending,
    didSendFail: sendMutation.isError,
    selectChat,
    setChatPrefs,
    setChatsMuted,
    markRead,
    markAllRead,
    clearActiveChat,
    loadMoreMessages,
    retryChats,
    retryMessages,
    send,
    dismissSendError,
  };
}
